import { ProgressBar } from '../../atoms/ProgressBar/ProgressBar';
import { Text } from '../../atoms/Text/Text';
import styles from './AICredits.module.css';

/**
 * Figma: `AI credits` (2130:59087) — the credit balance card, `space/8` padding
 * over a `Text sm` row and a `Progress Bar`.
 *
 * **Reworked 2026-09-22**, and every part of it changed:
 *
 * 1. **It moved.** It used to sit in `Sidebar Footer` above the workspace
 *    trigger; it is now the whole of the footer menu's lower `Bottom panel`,
 *    which supplies the `surface/neutral-subtle` band behind it.
 * 2. **The chrome is gone** — no `surface/card` fill and no `border/secondary`
 *    ring. The card is transparent now, which is why the band behind it exists.
 *    The 6px corner stays on the frame in Figma but has nothing left to round.
 * 3. **No `ai spark` glyph** beside the label.
 * 4. **Both labels stepped up** from `Text xs` to `Text sm/leading-none/Regular`,
 *    and the count is `text/tertiary` against the label's `text/primary`.
 * 5. **The bar** is `Size=lg, Type=AI Credit` — one colour for the filled run
 *    rather than the red → amber → green of `Default`. See `ProgressBarType`.
 *
 * The bar still shows what is **left** as a share of the allowance; Figma's
 * instance sits at 80%.
 *
 * Display only. The file draws no hover or pressed state and nothing opens from
 * it, so it is a plain region rather than a button.
 */
export type AICreditsProps = {
  /** Credits remaining. */
  left: number;
  /** The allowance `left` is counted down from; sets how full the bar is. */
  total: number;
  label?: string;
  className?: string;
};

export function AICredits({ left, total, label = 'AI credits', className }: AICreditsProps) {
  const remaining = Math.max(0, left);
  const percent = total > 0 ? (remaining / total) * 100 : 0;
  const count = remaining.toLocaleString('en-US');

  return (
    <section className={[styles.card, className].filter(Boolean).join(' ')} aria-label={label}>
      <div className={styles.row}>
        <Text size="sm" leading="none" weight="regular" color="primary">
          {label}
        </Text>
        <Text size="sm" leading="none" weight="regular" color="tertiary">
          {count} left
        </Text>
      </div>
      <ProgressBar
        className={styles.bar}
        type="aiCredit"
        value={percent}
        label={`${label}: ${count} of ${total.toLocaleString('en-US')} left`}
      />
    </section>
  );
}
